/**
 * ServiceTable — таблица systemd-сервисов хоста с действиями и логами.
 *
 * Backend: GET /api/v1/devops/services, POST /api/v1/devops/service/{name}/{action},
 * GET /api/v1/devops/service/{name}/logs.
 */

import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import {
  Search,
  RefreshCw,
  Play,
  Square,
  RotateCw,
  FileText,
  Loader2,
  ShieldAlert,
  X,
} from "lucide-react";
import {
  fetchNativeServices,
  fetchNativeServiceLogs,
  nativeServiceAction,
  type NativeService,
} from "../../lib/api";
import { cn } from "../../lib/cn";

type ServiceState = "all" | "active" | "running" | "failed" | "inactive";

export function ServiceTable() {
  const [items, setItems] = useState<NativeService[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState("");
  const [state, setState] = useState<ServiceState>("all");
  const [busy, setBusy] = useState<string | null>(null);
  const [confirm, setConfirm] = useState<{ name: string; action: string } | null>(null);
  const [logsOpen, setLogsOpen] = useState<string | null>(null);

  async function load() {
    try {
      setError(null);
      const r = await fetchNativeServices({ filter, state });
      setItems(r.items);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load();
  }, [filter, state]);

  async function doAction(
    name: string,
    action: "start" | "stop" | "restart" | "reload" | "enable" | "disable",
    confirmed: boolean = false,
  ) {
    setBusy(`${name}:${action}`);
    try {
      await nativeServiceAction(name, action, { confirm: confirmed });
      setConfirm(null);
      setTimeout(load, 300);
    } catch (e) {
      const msg = e instanceof Error ? e.message : String(e);
      if (msg.includes(" 409 ") || msg.includes("критич")) {
        setConfirm({ name, action });
      } else {
        setError(msg);
      }
    } finally {
      setBusy(null);
    }
  }

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-2">
        <div className="relative flex-1 min-w-[180px] max-w-md">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 size-3.5 text-fg-subtle" />
          <input
            type="text"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            placeholder="Имя сервиса или описание…"
            className="w-full pl-8 pr-3 py-1.5 rounded-md border border-border/60 bg-bg-card/70 text-small focus:outline-none focus:border-accent-500"
          />
        </div>
        <select
          value={state}
          onChange={(e) => setState(e.target.value as ServiceState)}
          className="px-2 py-1.5 rounded-md border border-border/60 bg-bg-card/70 text-small"
          aria-label="Состояние"
        >
          <option value="all">Все</option>
          <option value="running">Активные</option>
          <option value="failed">Failed</option>
          <option value="inactive">Inactive</option>
        </select>
        <button
          type="button"
          onClick={load}
          className="px-2 py-1.5 rounded-md border border-border/60 text-fg-muted hover:bg-bg-subtle text-small"
        >
          <RefreshCw className="size-3.5" />
        </button>
        <span className="text-small text-fg-muted ml-auto">{items.length} сервисов</span>
      </div>

      {error && (
        <div className="rounded-md border border-danger/40 bg-danger/5 px-3 py-2 text-small text-danger">
          {error}
        </div>
      )}

      {confirm && (
        <ConfirmServiceDialog
          name={confirm.name}
          action={confirm.action}
          onConfirm={() => doAction(confirm.name, confirm.action as "stop" | "restart" | "disable", true)}
          onCancel={() => setConfirm(null)}
        />
      )}

      <div className="rounded-modal border border-border/60 bg-bg-card/40 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-small font-mono">
            <thead className="bg-bg-subtle/60">
              <tr className="text-left text-fg-subtle text-[11px] uppercase tracking-wider">
                <th className="px-3 py-2">UNIT</th>
                <th className="px-3 py-2">LOAD</th>
                <th className="px-3 py-2">ACTIVE</th>
                <th className="px-3 py-2">SUB</th>
                <th className="px-3 py-2">DESCRIPTION</th>
                <th className="px-3 py-2 text-right">ACTIONS</th>
              </tr>
            </thead>
            <tbody>
              {loading && items.length === 0 ? (
                <tr>
                  <td colSpan={6} className="px-3 py-6 text-center text-fg-muted">
                    <Loader2 className="size-4 animate-spin inline-block mr-2" /> загрузка…
                  </td>
                </tr>
              ) : items.length === 0 ? (
                <tr>
                  <td colSpan={6} className="px-3 py-6 text-center text-fg-muted">
                    Нет сервисов под фильтр
                  </td>
                </tr>
              ) : (
                items.map((s) => (
                  <ServiceRow
                    key={s.name}
                    s={s}
                    busy={busy?.startsWith(s.name + ":") ? busy.split(":")[1] : null}
                    onAction={(a) => doAction(s.name, a)}
                    onLogs={() => setLogsOpen(s.name)}
                  />
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {logsOpen && (
        <LogsDrawer name={logsOpen} onClose={() => setLogsOpen(null)} />
      )}
    </div>
  );
}

function ServiceRow({
  s,
  busy,
  onAction,
  onLogs,
}: {
  s: NativeService;
  busy: string | null;
  onAction: (a: "start" | "stop" | "restart" | "reload") => void;
  onLogs: () => void;
}) {
  const isRunning = s.active === "active" && (s.sub === "running" || s.sub === "exited");
  const isFailed = s.active === "failed";
  const colorClass = isFailed
    ? "text-danger"
    : isRunning
      ? "text-success"
      : "text-fg-muted";
  return (
    <tr className="border-t border-border/30 hover:bg-bg-subtle/30">
      <td className="px-3 py-1.5 max-w-[260px] truncate text-fg">{s.name}</td>
      <td className="px-3 py-1.5 text-fg-muted">{s.load}</td>
      <td className={cn("px-3 py-1.5", colorClass)}>{s.active}</td>
      <td className="px-3 py-1.5 text-fg-muted">{s.sub}</td>
      <td className="px-3 py-1.5 max-w-[300px] truncate text-fg-muted" title={s.description}>
        {s.description}
      </td>
      <td className="px-3 py-1.5 text-right whitespace-nowrap">
        <ServiceActionBtn
          icon={<Play className="size-3" />}
          label="start"
          loading={busy === "start"}
          onClick={() => onAction("start")}
          tone="accent"
        />
        <ServiceActionBtn
          icon={<Square className="size-3" />}
          label="stop"
          loading={busy === "stop"}
          onClick={() => onAction("stop")}
          tone="warning"
        />
        <ServiceActionBtn
          icon={<RotateCw className="size-3" />}
          label="restart"
          loading={busy === "restart"}
          onClick={() => onAction("restart")}
          tone="warning"
        />
        <ServiceActionBtn
          icon={<FileText className="size-3" />}
          label="logs"
          loading={false}
          onClick={onLogs}
          tone="muted"
        />
      </td>
    </tr>
  );
}

function ServiceActionBtn({
  icon,
  label,
  loading,
  onClick,
  tone,
}: {
  icon: React.ReactNode;
  label: string;
  loading: boolean;
  onClick: () => void;
  tone: "accent" | "warning" | "muted";
}) {
  const cls = {
    accent: "border-accent-500/40 text-accent-500 hover:bg-accent-500/10",
    warning: "border-warning/40 text-warning hover:bg-warning/10",
    muted: "border-border/60 text-fg-muted hover:bg-bg-subtle",
  }[tone];
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={loading}
      className={cn(
        "ml-1 px-2 py-1 rounded-md text-[11px] border inline-flex items-center gap-1 disabled:opacity-50 disabled:cursor-wait",
        cls,
      )}
    >
      {loading ? <Loader2 className="size-3 animate-spin" /> : icon}
      {label}
    </button>
  );
}

function ConfirmServiceDialog({
  name,
  action,
  onConfirm,
  onCancel,
}: {
  name: string;
  action: string;
  onConfirm: () => void;
  onCancel: () => void;
}) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 4 }}
      animate={{ opacity: 1, y: 0 }}
      className="rounded-xl border border-warning/40 bg-warning/5 p-3 flex items-start gap-2"
    >
      <ShieldAlert className="size-4 mt-0.5 shrink-0 text-warning" />
      <div className="flex-1 min-w-0">
        <div className="font-medium text-small text-fg">
          Критичный сервис — подтвердите {action}
        </div>
        <div className="text-[12px] text-fg-muted mt-0.5">{name}</div>
        <div className="mt-2 flex gap-2">
          <button
            type="button"
            onClick={onConfirm}
            className="px-3 py-1.5 rounded-md text-small font-medium text-white"
            style={{ backgroundImage: "var(--grad-cta)" }}
          >
            Подтверждаю
          </button>
          <button
            type="button"
            onClick={onCancel}
            className="px-3 py-1.5 rounded-md text-small border border-border/60 text-fg-muted hover:bg-bg-subtle"
          >
            Отмена
          </button>
        </div>
      </div>
    </motion.div>
  );
}

function LogsDrawer({ name, onClose }: { name: string; onClose: () => void }) {
  const [logs, setLogs] = useState<string>("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [n, setN] = useState(200);

  async function load() {
    try {
      setLoading(true);
      setError(null);
      const r = await fetchNativeServiceLogs(name, n);
      setLogs(r.logs);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load();
  }, [name, n]);

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className="fixed inset-0 z-50 flex"
      onClick={onClose}
    >
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" />
      <motion.div
        initial={{ x: "100%" }}
        animate={{ x: 0 }}
        exit={{ x: "100%" }}
        transition={{ type: "spring", damping: 22, stiffness: 220 }}
        onClick={(e) => e.stopPropagation()}
        className="relative ml-auto h-full w-full max-w-3xl bg-bg shadow-2xl flex flex-col"
      >
        <div className="flex items-center gap-2 px-4 py-3 border-b border-border/40">
          <FileText className="size-4 text-accent-500" />
          <div className="flex-1 min-w-0">
            <div className="font-semibold truncate">{name}</div>
            <div className="text-[11px] text-fg-subtle font-mono uppercase tracking-wider">
              journalctl -u {name} -n {n}
            </div>
          </div>
          <select
            value={n}
            onChange={(e) => setN(Number(e.target.value))}
            className="px-2 py-1 rounded border border-border/60 bg-bg-card text-small"
          >
            <option value={50}>50 строк</option>
            <option value={200}>200 строк</option>
            <option value={1000}>1000 строк</option>
            <option value={5000}>5000 строк</option>
          </select>
          <button
            type="button"
            onClick={load}
            className="p-1.5 rounded border border-border/60 hover:bg-bg-subtle"
            title="Обновить"
          >
            <RefreshCw className="size-3.5" />
          </button>
          <button
            type="button"
            onClick={onClose}
            className="p-1.5 rounded border border-border/60 hover:bg-bg-subtle"
            title="Закрыть"
          >
            <X className="size-3.5" />
          </button>
        </div>
        <div className="flex-1 overflow-auto bg-bg-subtle/40 p-3">
          {loading && (
            <div className="text-fg-muted text-small">
              <Loader2 className="size-4 animate-spin inline-block mr-2" /> загрузка…
            </div>
          )}
          {error && (
            <div className="text-danger text-small">{error}</div>
          )}
          {!loading && !error && (
            <pre className="text-[12px] font-mono whitespace-pre-wrap text-fg leading-relaxed">
              {logs || "(пусто)"}
            </pre>
          )}
        </div>
      </motion.div>
    </motion.div>
  );
}
